import { useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { c } from 'ttag';

import { RegionState, SkeletonLoader } from '@freelancelabs/components';

import { AccountSocietiesDocument } from '../../graphql/__generated__/types';
import AccountSocietyPanel from './AccountSocietyPanel';
import { accountsRefusalOf } from './accountsRefusal';

interface Props {
    debouncedSearch: string;
    portalEnabled: boolean | null;
    moduleTotal: number | null;
}

// Case- and accent-insensitive substring match.
const matches = (haystack: string | null | undefined, needle: string) =>
    (haystack ?? '')
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase()
        .includes(needle);

// accountSocieties is a complete (unpaginated) set, so search and portal filter apply
// client-side; the portal filter hides a company but does not filter its expansion, whose
// counter is the perimeter total (accountCount).
const GroupedAccounts = ({ debouncedSearch, portalEnabled, moduleTotal }: Props) => {
    const [openId, setOpenId] = useState<string | null>(null);

    const { data, loading, error } = useQuery(AccountSocietiesDocument, { fetchPolicy: 'cache-and-network' });

    const needle = debouncedSearch
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLowerCase();

    const societies = (data?.accountSocieties ?? []).filter((society) => {
        if (needle && !matches(society.rootOrganizationName, needle) && !matches(society.siren, needle)) {
            return false;
        }
        // Null is not zero: a company whose count the API did not state stays visible.
        if (portalEnabled === true) {
            return society.portalEnabledCount === null || society.portalEnabledCount > 0;
        }
        if (portalEnabled === false) {
            if (society.accountCount === null || society.portalEnabledCount === null) {
                return true;
            }
            return society.accountCount - society.portalEnabledCount > 0;
        }
        return true;
    });

    const refusal = error ? accountsRefusalOf(error) : null;
    const filtered = Boolean(debouncedSearch) || portalEnabled !== null;

    return (
        <div className="flex flex-col gap-6">
            <section className="flex flex-col gap-1">
                {refusal ? (
                    <RegionState tone="error" title={refusal.title} description={refusal.guidance} />
                ) : loading && !data ? (
                    <div aria-label={c('Account societies').t`Loading the parent companies…`} aria-busy="true">
                        {[0, 1, 2, 3].map((index) => (
                            <SkeletonLoader
                                key={index}
                                height={56}
                                className="mb-2 rounded-[var(--portal-section-radius)]"
                            />
                        ))}
                    </div>
                ) : societies.length === 0 ? (
                    <RegionState
                        title={
                            filtered
                                ? c('Account societies').t`No parent company matches these criteria.`
                                : c('Account societies').t`No parent company in your portfolio.`
                        }
                        description={
                            filtered
                                ? c('Account societies')
                                      .t`The search reads the company name and its SIREN. A single-account client is not a company here: look for it in the flat accounts below.`
                                : c('Account societies')
                                      .t`A parent company groups several accounts of one client. Accounts that stand on their own are listed below.`
                        }
                    />
                ) : (
                    societies.map((society) => (
                        <AccountSocietyPanel
                            key={society.rootOrganizationId}
                            society={society}
                            open={openId === society.rootOrganizationId}
                            onOpenChange={(open) => setOpenId(open ? society.rootOrganizationId : null)}
                            moduleTotal={moduleTotal}
                        />
                    ))
                )}
            </section>
        </div>
    );
};

export default GroupedAccounts;
